import { useState } from 'react';
import type { Invoice, InvoiceTransitionAction } from '@/types';
import { AlertCircle, CheckCircle2, Undo2, XCircle } from 'lucide-react';

/**
 * Review-Aktionen je Status + Rolle. Buttons erscheinen nur, wenn der
 * aktuelle Status die Aktion erlaubt (Spiegel der TRANSITIONS-Map im
 * InvoiceController) und der Nutzer berechtigt ist:
 *   - admin (Buchhaltung): alle Aktionen
 *   - approver: approve/request_correction/reject
 *   - Bauleiter des BV (can_review, Rolle 'user'): approve/request_correction
 * Pflichtkommentar-Aktionen (Korrektur, Ablehnung) öffnen ein
 * Kommentarfeld — das Backend lehnt sonst mit 400 ab.
 */
export function InvoiceReviewActions({
  invoice,
  isAdmin,
  isApprover,
  isBusy,
  onTransition,
}: {
  invoice: Invoice;
  isAdmin: boolean;
  isApprover: boolean;
  isBusy: boolean;
  onTransition: (action: InvoiceTransitionAction, comment?: string) => Promise<void>;
}) {
  const [commentFor, setCommentFor] = useState<InvoiceTransitionAction | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  if (!isAdmin && !isApprover && !invoice.can_review) {
    return null;
  }

  const runDirect = async (action: InvoiceTransitionAction) => {
    setError('');
    try {
      await onTransition(action);
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Statuswechsel');
    }
  };

  const runWithComment = async () => {
    if (commentFor === null) return;
    const value = comment.trim();
    if (!value) return; // Pflichtkommentar
    setError('');
    try {
      await onTransition(commentFor, value);
      setCommentFor(null);
      setComment('');
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Statuswechsel');
    }
  };

  // Aktionen je Status — Reihenfolge = Reihenfolge der Buttons.
  const actions: Array<{ action: InvoiceTransitionAction; label: string; style: 'primary' | 'secondary' | 'danger' | 'success' }> = [];

  if (isAdmin) {
    if (invoice.status === 'erfasst') {
      actions.push({ action: 'formal_check', label: 'Formal prüfen', style: 'primary' });
    }
    // Ausgangsrechnungen überspringen die formale Prüfung: Freigabe
    // direkt ab 'erfasst' (Backend erlaubt approve ab erfasst für ausgang).
    if (invoice.status === 'erfasst' && invoice.direction === 'ausgang') {
      actions.push({ action: 'approve', label: 'Freigeben', style: 'success' });
    }
    if (invoice.status === 'abgelehnt' || invoice.status === 'korrektur_erforderlich') {
      actions.push({ action: 'reopen_review', label: 'Erneut zur Prüfung', style: 'secondary' });
    }
    if (invoice.status === 'freigegeben') {
      actions.push({ action: 'mark_exported', label: 'Als übergeben markieren', style: 'primary' });
    }
    if (invoice.status === 'an_lexware_uebergeben') {
      actions.push({ action: 'reopen_lexware', label: 'Übergabe zurücknehmen', style: 'secondary' });
      actions.push(
        invoice.payment_status === 'offen'
          ? { action: 'mark_paid', label: 'Als bezahlt markieren', style: 'success' }
          : { action: 'mark_unpaid', label: 'Bezahlt zurücknehmen', style: 'secondary' }
      );
    }
  }

  const canReviewActions = isAdmin || isApprover || invoice.can_review;
  if (canReviewActions) {
    if (invoice.status === 'formal_geprueft') {
      actions.push({ action: 'approve', label: 'Freigeben', style: 'success' });
    }
    if (invoice.status === 'formal_geprueft' || invoice.status === 'freigegeben') {
      actions.push({ action: 'request_correction', label: 'Korrektur anfordern', style: 'secondary' });
    }
    // Ablehnen: nur admin/approver (Bauleiter bitten um Korrektur oder
    // geben frei — die finale Ablehnung ist Buchhaltungs-Sache).
    if ((isAdmin || isApprover) && (invoice.status === 'erfasst' || invoice.status === 'formal_geprueft')) {
      actions.push({ action: 'reject', label: 'Ablehnen', style: 'danger' });
    }
  }

  if (actions.length === 0) {
    return null;
  }

  const styleClasses: Record<string, string> = {
    primary: 'btn btn-primary',
    secondary: 'btn btn-secondary',
    success: 'btn btn-primary',
    danger: 'btn btn-danger',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <button
            key={action.action}
            onClick={() =>
              action.action === 'request_correction' || action.action === 'reject'
                ? (setCommentFor(action.action), setComment(''), setError(''))
                : runDirect(action.action)
            }
            disabled={isBusy}
            className={`${styleClasses[action.style]} inline-flex items-center gap-1.5 text-sm disabled:opacity-50`}
          >
            {action.action === 'approve' || action.action === 'mark_paid' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : action.action === 'reject' ? (
              <XCircle className="h-4 w-4" />
            ) : action.action === 'reopen_review' || action.action === 'reopen_lexware' || action.action === 'mark_unpaid' ? (
              <Undo2 className="h-4 w-4" />
            ) : null}
            {action.label}
          </button>
        ))}
      </div>

      {commentFor && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <label className="label">
            {commentFor === 'request_correction' ? 'Begründung der Korrektur *' : 'Begründung der Ablehnung *'}
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            className="input"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={async () => {
                await runWithComment();
              }}
              disabled={!comment.trim()}
              className="btn btn-primary text-sm"
            >
              Abschicken
            </button>
            <button
              onClick={() => {
                setCommentFor(null);
                setComment('');
                setError('');
              }}
              className="btn btn-secondary text-sm"
            >
              Abbrechen
            </button>
          </div>
          {!comment.trim() && (
            <p className="mt-1 text-xs text-gray-500">Ein Kommentar ist für diese Aktion erforderlich.</p>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}